// components/Common/CommonToaster.js
import React, { useEffect } from "react";
import { CheckCircle, XCircle, AlertTriangle, Info, X } from "lucide-react";

// ── Class strings ─────────────────────────────────────────────────────────
// Converted to Tailwind 2026-08-25 (Phase 5); CommonToaster.scss deleted with it.
//
// This is the clearest case in the phase for why §4.4 gives every status family a
// `-soft` background AND a `-fg` ink. The sheet declared FOUR tone variants and
// then declared all four AGAIN inside `:root[data-theme="dark"]` — eight blocks,
// 48 colour literals, four of which (`#2563eb`, `#1e40af`, `#60a5fa`, `#bfdbfe`)
// were the v1 blue that nothing else in the app still uses. The token pairs
// collapse all of it to one map, and the dark blocks are simply gone: the token
// already changed underneath.
//
// ⚠️ The info tone shifts hue slightly. `--info` is `199 89% 48%` — sky — where
// this toast wrote `#2563eb`, a blue. That is the point: there is one info
// colour in the app now, and it is the one the badges and banners already use.
//
// Two keyframes moved to tailwind.config.js (`toast-slide-in`, `countdown`)
// rather than staying in a co-located sheet, which would have made this file
// Tailwind AND SCSS and tripped the `mixed` check.
const TONE = {
  success: {
    icon: CheckCircle,
    label: "Success",
    box: "tw-bg-success-soft tw-border-success/30",
    accent: "tw-text-success",
    message: "tw-text-success-fg",
    bar: "tw-bg-success",
  },
  error: {
    icon: XCircle,
    label: "Error",
    box: "tw-bg-danger-soft tw-border-danger/30",
    accent: "tw-text-danger",
    message: "tw-text-danger-fg",
    bar: "tw-bg-danger",
  },
  warning: {
    icon: AlertTriangle,
    label: "Warning",
    box: "tw-bg-warning-soft tw-border-warning/30",
    accent: "tw-text-warning",
    message: "tw-text-warning-fg",
    bar: "tw-bg-warning",
  },
  info: {
    icon: Info,
    label: "Info",
    box: "tw-bg-info-soft tw-border-info/30",
    accent: "tw-text-info",
    message: "tw-text-info-fg",
    bar: "tw-bg-info",
  },
};

// react-bootstrap's names, because the call sites were written beside it: about
// fifty pass `type: "danger"` for a failure. Unaliased, that fell through to the
// success tone and every one of those failures rendered as a green "Success".
const TYPE_ALIASES = { danger: "error", warn: "warning" };

/** Resolve `type` to a tone. Unknown → neutral info, never success, and warn. */
function resolveTone(type) {
  const key = TYPE_ALIASES[type] || type;
  if (TONE[key]) return TONE[key];
  if (process.env.NODE_ENV !== "production") {
    // eslint-disable-next-line no-console
    console.warn(`CommonToaster: unknown type "${type}" — use success, error, warning or info.`);
  }
  return TONE.info;
}

const POSITION = {
  "top-end": "tw-top-4 tw-right-4",
  "top-center": "tw-top-4 tw-left-1/2 tw--translate-x-1/2",
  "bottom-end": "tw-bottom-4 tw-right-4",
};

// A toast FLOATS over the page, so --popover-shadow rather than --card-shadow —
// the same distinction IncomingCallPopup's conversion settled. It also replaces
// the two-layer rgba() shadow that had to be restated in dark mode.
const CARD =
  "tw-pointer-events-auto tw-relative tw-flex tw-items-start tw-gap-2.5 tw-overflow-hidden " +
  "tw-rounded tw-border tw-border-solid tw-px-4 tw-py-3.5 tw-shadow-popover " +
  "tw-animate-toast-slide-in tw-min-w-[320px] tw-max-w-[440px] " +
  "max-[480px]:tw-min-w-0 max-[480px]:tw-max-w-[calc(100vw-2rem)]";

// --elevate-2 rather than a literal wash: it is rgba(0,0,0,.08) light and
// rgba(255,255,255,.09) dark, which is what the two hand-written hover rules
// were reaching for one theme at a time.
const CLOSE =
  "tw--my-1 tw--me-1 tw-flex tw-h-7 tw-w-7 tw-shrink-0 tw-cursor-pointer tw-items-center " +
  "tw-justify-center tw-rounded-[0.375rem] tw-border-0 tw-bg-transparent " +
  "tw-transition-colors tw-duration-150 hover:tw-bg-[var(--elevate-2)]";

const CommonToaster = ({ show, message, type = "success", onClose, position = "top-end", delay = 5000 }) => {
  const tone = resolveTone(type);
  const Icon = tone.icon;

  useEffect(() => {
    if (!show || !delay) return;
    const timer = setTimeout(() => onClose?.(), delay);
    return () => clearTimeout(timer);
  }, [show, delay, onClose]);

  if (!show) return null;

  return (
    <div className={`tw-pointer-events-none tw-fixed tw-z-toast ${POSITION[position] || POSITION["top-end"]}`}>
      <div className={`${CARD} ${tone.box}`} role="alert">
        <div className={`tw-mt-px tw-flex tw-shrink-0 tw-items-center ${tone.accent}`}>
          <Icon size={18} />
        </div>
        <div className="tw-min-w-0 tw-flex-1">
          {/* The tone is otherwise colour and icon only. A sibling, so the
              message's own element still reads exactly as the message. */}
          <span className="tw-sr-only">{tone.label}: </span>
          <span className={`tw-break-words tw-text-sm tw-font-medium tw-leading-[1.4] ${tone.message}`}>
            {message}
          </span>
        </div>
        <button className={`${CLOSE} ${tone.accent}`} onClick={onClose} aria-label="Close">
          <X size={16} />
        </button>
        {/* Duration comes from the `delay` prop, so it is an inline longhand
            overriding the utility's shorthand — see tailwind.config.js. */}
        <div
          className={`tw-absolute tw-bottom-0 tw-left-0 tw-h-[3px] tw-w-full tw-origin-left tw-animate-countdown ${tone.bar}`}
          style={{ animationDuration: `${delay}ms` }}
        />
      </div>
    </div>
  );
};

export default CommonToaster;
